import React, { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import {
  createRegistration,
  createStudent,
  listExtraActivities,
  listFrequencies,
  printComprovativo,
  uploadStudentPhotoCreate,
} from "../services/student";
import { levelKey } from "../utils/classesLabels";
import { getSucursalInfo, getUserInfo, hasSpecialHourSchedule } from "../services/auth";
import { listPaymentTypes } from "../services/paymentTypes";
import {
  DISCOUNT_OPTIONS,
  applyDiscount,
  buildDiscountPayloadFields,
  computeBaseTotals,
} from "../utils/studentPricing";
import WizardSteps from "./shared/WizardSteps";
import MultiSelectField from "./shared/MultiSelectField";
import StepCard from "./shared/StepCard";

const TOTAL_STEPS = 5; // 5 form steps + a 6th "success" screen (done state)

const currentYear = new Date().getFullYear();

const MONTH_NAMES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const initialState = {
  imagePreview: "",
  pictureFilename: "",
  uploadingPhoto: false,
  name: "",
  code: "",
  birthDay: "",
  birthMonth: "",
  birthYear: "",
  gender: "",
  docType: "",
  docNumber: "",
  studentAddress: "",
  hasDesease: "0",
  deseaseDesc: "",
  isNew: true,
  enrollmentYear: currentYear,
  isAlergicToFood: false,
  alergicToFood: "",
  isAlergicToMedicine: false,
  alergicToMedicine: "",

  fatherName: "",
  motherName: "",
  fatherContact: "",
  motherContact: "",

  kinshipDegree: "",
  carierName: "",
  jobLocation: "",
  carierContact: "",
  carierSecondContact: "",
  address: "",

  selectedYear: currentYear,
  frequency: "",
  extraCuricularActivities: [],
  paymentMethod: "",
  scheduleType: "7h-12h",

  scholarshipType: "none",
  discountType: "percentage",
  discountAppliesTo: "registration",
  discount: 0,
  registrationDiscount: 0,
  monthlyDiscount: 0,
  exemptRegistration: false,

  originalRegistrationValue: 0,
  originalMonthlyPayment: 0,
  registrationValue: 0,
  monthlyPayment: 0,
  total: 0,
  registrationAmountPaid: 0,
};

const money = (v) => (parseFloat(v) || 0).toFixed(2);

// Confirmation-step review card: a titled bg-neutral-50 panel of label/value
// rows, skipping any row whose value is empty so optional fields don't leave
// a blank line in the final review.
const ReviewCard = ({ icon, title, children }) => (
  <div className='col-md-6'>
    <div className='p-16 radius-12 bg-neutral-50 h-100'>
      <div className='d-flex align-items-center gap-8 mb-12'>
        <Icon icon={icon} className='text-primary-600 text-lg' />
        <h6 className='text-sm text-neutral-600 mb-0'>{title}</h6>
      </div>
      <div className='d-flex flex-column gap-4'>{children}</div>
    </div>
  </div>
);

const ReviewRow = ({ label, value }) =>
  value === "" || value === null || value === undefined ? null : (
    <div className='d-flex align-items-center justify-content-between gap-12 py-4 border-bottom border-neutral-100'>
      <span className='text-secondary-light text-sm'>{label}</span>
      <span className='text-neutral-900 text-sm fw-medium text-end'>{value}</span>
    </div>
  );

const CadastrarEstudanteLayer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [form, setForm] = useState(initialState);
  const [frequencies, setFrequencies] = useState([]);
  const [allActivities, setAllActivities] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [createdIds, setCreatedIds] = useState({ registrationId: null, studentId: null });

  useEffect(() => {
    listFrequencies()
      .then((resp) => setFrequencies(resp.data || []))
      .catch(() => setFrequencies([]));
    listExtraActivities()
      .then((resp) => setAllActivities(resp.data || []))
      .catch(() => setAllActivities([]));
    // Tipos de Pagamento por sucursal (SGE_PAYMENT_TYPES) - ver services/paymentTypes.
    listPaymentTypes()
      .then((resp) => setPaymentMethods(resp.data || []))
      .catch(() => setPaymentMethods([]));
  }, []);

  const stepLabels = [
    t("estudante.wizard.step.personal"),
    t("estudante.wizard.step.filiation"),
    t("estudante.wizard.step.guardian"),
    t("estudante.wizard.step.enrollment"),
    t("estudante.wizard.step.confirm"),
    t("estudante.wizard.step.success"),
  ];

  const displayStep = done ? 6 : currentStep;

  const setField = (field) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const birthDate = useMemo(() => {
    if (!form.birthDay || !form.birthMonth || !form.birthYear) return "";
    return `${form.birthYear}-${String(form.birthMonth).padStart(2, "0")}-${String(
      form.birthDay,
    ).padStart(2, "0")}`;
  }, [form.birthDay, form.birthMonth, form.birthYear]);

  const setChecked = (field) => (e) => {
    const checked = e.target.checked;
    setForm((f) => ({ ...f, [field]: checked }));
  };

  const selectedFrequency = useMemo(
    () => frequencies.find((f) => String(f.id) === String(form.frequency)),
    [frequencies, form.frequency],
  );

  const activitiesForFrequency = useMemo(
    () => allActivities.filter((a) => String(a.levelId) === String(selectedFrequency?.id)),
    [allActivities, selectedFrequency],
  );

  const selectedActivitiesKey = form.extraCuricularActivities.join(",");

  const selectedActivityNames = useMemo(
    () =>
      activitiesForFrequency
        .filter((a) => form.extraCuricularActivities.includes(String(a.id)))
        .map((a) => a.name)
        .join(", "),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activitiesForFrequency, selectedActivitiesKey],
  );

  const scholarshipLabels = {
    none: t("estudante.scholarship.type.none"),
    discount: t("estudante.scholarship.type.discount"),
    partial: t("estudante.scholarship.type.partial"),
    full: t("estudante.scholarship.type.full"),
  };

  // Recompute registrationValue/monthlyPayment/total whenever the class,
  // activities, "is new enrolment" flag or any discount/scholarship input
  // changes — mirrors handleSelectClass + handleSelectDiscount in the
  // original sge_infantil_web/src/pages/student/create/index.js.
  useEffect(() => {
    const selectedActs = activitiesForFrequency.filter((a) =>
      form.extraCuricularActivities.includes(String(a.id)),
    );
    const base = computeBaseTotals({
      freq: selectedFrequency,
      isNew: form.isNew,
      selectedActivities: selectedActs,
    });
    const originalRegistrationValue = base.registrationValue;
    const originalMonthlyPayment = base.monthlyPayment;

    let next;
    if (form.scholarshipType === "none") {
      next = {
        originalRegistrationValue,
        originalMonthlyPayment,
        registrationValue: base.registrationValue,
        monthlyPayment: base.monthlyPayment,
        total: base.total,
      };
    } else {
      const discounted = applyDiscount({
        scholarshipType: form.scholarshipType,
        discountType: form.discountType,
        discountAppliesTo: form.discountAppliesTo,
        discount: form.discount,
        registrationDiscount: form.registrationDiscount,
        monthlyDiscount: form.monthlyDiscount,
        exemptRegistration: form.exemptRegistration,
        originalRegistrationValue,
        originalMonthlyPayment,
      });
      next = { originalRegistrationValue, originalMonthlyPayment, ...discounted };
    }

    // O valor pago da Inscrição nasce igual ao valor (já descontado) calculado acima — pagar
    // tudo continua a ser o comportamento por omissão — e é reposto sempre que o valor da
    // Inscrição muda (classe/desconto/bolseiro), para nunca deixar um "Valor Pago" obsoleto.
    next.registrationAmountPaid = next.registrationValue;

    setForm((f) => ({ ...f, ...next }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedFrequency,
    form.isNew,
    selectedActivitiesKey,
    form.scholarshipType,
    form.discountType,
    form.discountAppliesTo,
    form.discount,
    form.registrationDiscount,
    form.monthlyDiscount,
    form.exemptRegistration,
  ]);

  const handleSelectKinship = (e) => {
    const kinshipDegree = e.target.value;
    setForm((f) => {
      if (kinshipDegree === "PAI") {
        return { ...f, kinshipDegree, carierName: f.fatherName, carierContact: f.fatherContact };
      }
      if (kinshipDegree === "MAE") {
        return { ...f, kinshipDegree, carierName: f.motherName, carierContact: f.motherContact };
      }
      return { ...f, kinshipDegree, carierName: "", carierContact: "" };
    });
  };

  const handleScholarshipType = (e) => {
    const value = e.target.value;
    setForm((f) => ({
      ...f,
      scholarshipType: value,
      discount: 0,
      registrationDiscount: 0,
      monthlyDiscount: 0,
      exemptRegistration: false,
    }));
  };

  const uploadPhotoFile = async (file, previewDataUrl) => {
    setForm((f) => ({ ...f, imagePreview: previewDataUrl, uploadingPhoto: true }));
    try {
      const resp = await uploadStudentPhotoCreate(file);
      setForm((f) => ({
        ...f,
        pictureFilename: resp.data?.originalFileName || "",
        uploadingPhoto: false,
      }));
    } catch (err) {
      setForm((f) => ({ ...f, uploadingPhoto: false }));
      toast.error(t("estudante.error.photo.upload"));
    }
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => uploadPhotoFile(file, reader.result);
    reader.readAsDataURL(file);
  };

  // "Tirar Foto" — mirrors sge_infantil_web's PictureUploader webcam capture
  // (getUserMedia → live preview → capture frame to canvas → upload).
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      streamRef.current = stream;
      setCameraActive(true);
      // Wait for the <video> element to mount before attaching the stream.
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      }, 0);
    } catch (err) {
      toast.error(t("estudante.error.camera"));
    }
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setCameraActive(false);
  };

  useEffect(() => {
    return () => streamRef.current?.getTracks().forEach((track) => track.stop());
  }, []);

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg");
    stopCamera();
    canvas.toBlob((blob) => {
      const file = new File([blob], "captured-photo.jpg", { type: "image/jpeg" });
      uploadPhotoFile(file, dataUrl);
    }, "image/jpeg");
  };

  const validateStep = (step) => {
    if (step === 1) {
      if (!form.name || !birthDate || !form.gender || !form.docType || !form.studentAddress) {
        toast.error(t("validation.required"));
        return false;
      }
      if (form.docType !== "Nenhum" && !form.docNumber) {
        toast.error(t("validation.required"));
        return false;
      }
      if (form.hasDesease === "1" && !form.deseaseDesc) {
        toast.error(t("validation.required"));
        return false;
      }
      return true;
    }
    if (step === 2) {
      if (!form.fatherName || !form.motherName) {
        toast.error(t("validation.required"));
        return false;
      }
      return true;
    }
    if (step === 3) {
      if (!form.kinshipDegree || !form.carierName || !form.carierContact || !form.address) {
        toast.error(t("validation.required"));
        return false;
      }
      if (form.carierContact.length !== 9) {
        toast.error(t("estudante.validation.contact.length"));
        return false;
      }
      if (form.carierSecondContact && form.carierSecondContact.length !== 9) {
        toast.error(t("estudante.validation.contact.length"));
        return false;
      }
      return true;
    }
    if (step === 4) {
      if (!form.selectedYear || !form.frequency || !form.paymentMethod) {
        toast.error(t("validation.required"));
        return false;
      }
      const amountPaid = parseFloat(form.registrationAmountPaid);
      if (!(amountPaid > 0) || amountPaid > parseFloat(form.registrationValue) + 0.01) {
        toast.error(t("estudante.validation.registrationAmountPaid"));
        return false;
      }
      return true;
    }
    return true;
  };

  const nextStep = () => {
    if (!validateStep(currentStep)) return;
    if (currentStep < TOTAL_STEPS) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const restart = () => {
    setForm(initialState);
    setCurrentStep(1);
    setDone(false);
    setCreatedIds({ registrationId: null, studentId: null });
  };

  const submit = async () => {
    setSubmitting(true);
    const userId = getUserInfo()?.id;
    const sucursal = getSucursalInfo();

    const carier = {
      name: form.carierName,
      kinshipDegree: form.kinshipDegree,
      contact: form.carierContact,
      secondContact: form.carierSecondContact,
      workPlace: form.jobLocation,
      createdBy: userId,
      activatedBy: userId,
    };

    try {
      const studentResp = await createStudent({
        name: form.name,
        code: form.code,
        address: form.address,
        sex: form.gender,
        sucursal,
        imageUrl: form.pictureFilename,
        birthDate,
        docType: form.docType,
        currentMonthlyPayment: form.monthlyPayment,
        studentAddress: form.studentAddress,
        level: form.frequency,
        docNumber: form.docNumber,
        alergicToFood: form.alergicToFood,
        alergicToMedicine: form.alergicToMedicine,
        motherContact: form.motherContact,
        fatherContact: form.fatherContact,
        motherName: form.motherName,
        fatherName: form.fatherName,
        carier,
        sucursalId: sucursal?.id,
        createdBy: userId,
        activatedBy: userId,
        sufferFromAnyIllness: form.deseaseDesc,
        enrollmentYear: form.enrollmentYear,
        isNew: form.isNew,
      });

      const createdStudent = studentResp.data;

      const extraActivities = activitiesForFrequency
        .filter((ea) => form.extraCuricularActivities.includes(String(ea.id)))
        .map((ea) => ({ id: ea.id, name: ea.name, recurringAmount: ea.recuringAmount }));

      const freqRegistrationValue = form.isNew
        ? parseFloat(selectedFrequency?.registrationValue) || 0
        : parseFloat(selectedFrequency?.recurigRegistrationValue) || 0;
      const freqMonthlyPayment = parseFloat(selectedFrequency?.monthlyPayment) || 0;

      const { discountToSend, registrationDiscountToSend, monthlyDiscountToSend } =
        buildDiscountPayloadFields({
          discountType: form.discountType,
          discount: form.discount,
          registrationDiscount: form.registrationDiscount,
          monthlyDiscount: form.monthlyDiscount,
          freqRegistrationValue,
          freqMonthlyPayment,
        });

      const registrationResp = await createRegistration({
        monthlyPayment: form.monthlyPayment,
        totalPaid: form.registrationValue,
        discount: discountToSend,
        studentId: createdStudent.id,
        student: createdStudent,
        isNew: form.isNew,
        year: form.selectedYear,
        enrollmentYear: form.enrollmentYear,
        sucursal,
        classId: form.frequency,
        scheduleType: form.scheduleType,
        createdBy: userId,
        activatedBy: userId,
        extraconfigs: extraActivities,
        corseId: form.frequency,
        degree: "",
        level: form.frequency,
        lastRegistrationYear: form.selectedYear,
        hasRegistrationRegularized: false,
        ivaPercentage: 0,
        unregularized: true,
        mobile: createdStudent.contact || "",
        extraCurricularTotal: 0,
        includeDiscount: form.scholarshipType !== "none",
        isDiscountNumeric: true,
        applyToRegistration: form.discountAppliesTo === "registration",
        applyToPayment: form.discountAppliesTo === "payment",
        applyToBoth: form.discountAppliesTo === "both",
        scholarshipType: form.scholarshipType,
        isScholarship: form.scholarshipType !== "none",
        isFullScholarship: form.scholarshipType === "full",
        isPartialScholarship: form.scholarshipType === "partial",
        exemptRegistration: form.exemptRegistration,
        registrationDiscount: registrationDiscountToSend,
        monthlyDiscount: monthlyDiscountToSend,
        originalRegistrationValue: form.originalRegistrationValue,
        originalMonthlyPayment: form.originalMonthlyPayment,
        registrationValue: form.registrationValue,
        total: form.total,
        registrationTaxValue: form.registrationValue,
        registrationTaxAmountPaid: form.registrationAmountPaid,
        registrationTaxPaymentMethod: form.paymentMethod,
      });

      setCreatedIds({
        registrationId: registrationResp.data?.id,
        studentId: registrationResp.data?.studentId || createdStudent.id,
      });
      setDone(true);
    } catch (err) {
      if (
        err?.response?.data?.error === "A student with the given mobile number already exists"
      ) {
        toast.error(t("estudante.error.duplicate.mobile"));
      } else {
        toast.error(t("global.error.message"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrintComprovativo = async () => {
    if (!createdIds.registrationId) return;
    setPrinting(true);
    try {
      const resp = await printComprovativo({
        registrationId: createdIds.registrationId,
        studentId: createdIds.studentId,
        isRenewal: !form.isNew,
      });
      if (resp.data?.pdf) {
        window.open(resp.data.pdf, "_blank", "noopener,noreferrer");
      } else {
        toast.error(t("estudante.error.print"));
      }
    } catch (err) {
      toast.error(t("estudante.error.print"));
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />
      <div className='col-12'>
        <div className='card'>
          <div className='card-body'>
            <h6 className='mb-4 text-xl'>{t("estudante.wizard.title")}</h6>
            <p className='text-neutral-500'>{t("estudante.wizard.subtitle")}</p>

            <div className='form-wizard'>
              <WizardSteps steps={stepLabels} currentStep={displayStep} />

              {!done && currentStep === 1 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='mb-24'>
                    <h6 className='text-md mb-4'>{t("estudante.wizard.step.personal")}</h6>
                    <p className='text-secondary-light text-sm mb-0'>
                      {t("estudante.personal.subtitle")}
                    </p>
                  </div>

                  <div className='row gy-4'>
                    <div className='col-lg-3 col-md-4'>
                      <div className='d-flex flex-column align-items-center gap-16 p-24 radius-16 border border-neutral-100 h-100'>
                        <div className='position-relative'>
                          <div className='w-160-px h-160-px rounded-circle d-flex align-items-center justify-content-center overflow-hidden bg-neutral-50 border border-3 border-primary-50'>
                            {cameraActive ? (
                              <video
                                ref={videoRef}
                                muted
                                playsInline
                                className='w-100 h-100 object-fit-cover'
                              />
                            ) : form.imagePreview ? (
                              <img
                                src={form.imagePreview}
                                alt=''
                                className='w-100 h-100 object-fit-cover'
                              />
                            ) : (
                              <Icon
                                icon='solar:user-rounded-outline'
                                className='text-neutral-300'
                                style={{ fontSize: 72 }}
                              />
                            )}
                          </div>
                          {!cameraActive && (
                            <label
                              htmlFor='estudanteImageUpload'
                              className='position-absolute bottom-0 end-0 w-40-px h-40-px rounded-circle bg-primary-600 text-white d-flex align-items-center justify-content-center border border-3 border-white mb-0'
                              style={{ cursor: "pointer" }}
                              title={t("estudante.photo.uploadPhoto")}
                            >
                              <Icon icon='solar:camera-outline' className='text-lg' />
                            </label>
                          )}
                          <input
                            type='file'
                            id='estudanteImageUpload'
                            accept='.png,.jpg,.jpeg'
                            hidden
                            onChange={handlePhotoChange}
                          />
                        </div>

                        <div className='text-center'>
                          <span className='text-sm fw-semibold text-neutral-600 d-block'>
                            {t("estudante.field.photo")}
                          </span>
                          <span className='text-xs text-secondary-light'>
                            {t("estudante.field.photo.placeholder")}
                          </span>
                        </div>

                        {cameraActive ? (
                          <div className='d-flex gap-8 w-100'>
                            <button
                              type='button'
                              className='btn btn-primary-600 btn-sm w-50'
                              onClick={capturePhoto}
                            >
                              <i className='ri-camera-line' /> {t("estudante.photo.capture")}
                            </button>
                            <button
                              type='button'
                              className='btn border border-danger-600 text-danger-600 btn-sm w-50'
                              onClick={stopCamera}
                            >
                              {t("global.cancel")}
                            </button>
                          </div>
                        ) : (
                          <button
                            type='button'
                            className='btn border border-primary-600 text-primary-600 btn-sm w-100 d-flex align-items-center justify-content-center gap-2'
                            onClick={startCamera}
                          >
                            <i className='ri-camera-line' /> {t("estudante.photo.takePhoto")}
                          </button>
                        )}

                        {form.uploadingPhoto && (
                          <span className='text-xs text-secondary-light'>
                            {t("estudante.photo.uploading")}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className='col-lg-9 col-md-8'>
                      <StepCard
                        icon='solar:user-id-outline'
                        title={t("estudante.personal.identification.title")}
                        accent='primary'
                      >
                        <div className='row gy-3'>
                          <div className='col-sm-6'>
                            <label className='form-label'>{t("estudante.field.name")} <span className='required-asterisk'>*</span></label>
                            <div className="icon-field">
                              <span className="icon">
                                <Icon icon="f7:person" />
                              </span>
                              <input
                                type='text'
                                className='form-control'
                                placeholder={t("estudante.field.name.placeholder")}
                                value={form.name}
                                onChange={setField("name")}
                              />
                            </div>
                          </div>
                          <div className='col-sm-6'>
                            <label className='form-label'>{t("estudante.field.code")}</label>
                            <div className="icon-field">
                              <span className="icon">
                                <Icon icon="solar:hashtag-square-outline" />
                              </span>
                              <input
                                type='text'
                                className='form-control'
                                placeholder={t("estudante.field.code.placeholder")}
                                value={form.code}
                                onChange={setField("code")}
                              />
                            </div>
                          </div>
                          <div className='col-sm-8'>
                            <label className='form-label'>
                              {t("global.birth.date")} <span className='required-asterisk'>*</span>
                            </label>
                            <div className='row gx-2'>
                              <div className='col-4'>
                                <select
                                  className='form-control form-select'
                                  value={form.birthDay}
                                  onChange={setField("birthDay")}
                                >
                                  <option value=''>{t("global.birth.day")}</option>
                                  {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                                    <option key={day} value={day}>
                                      {day}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className='col-4'>
                                <select
                                  className='form-control form-select'
                                  value={form.birthMonth}
                                  onChange={setField("birthMonth")}
                                >
                                  <option value=''>{t("global.birth.month")}</option>
                                  {MONTH_NAMES.map((name, idx) => (
                                    <option key={name} value={idx + 1}>
                                      {name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className='col-4'>
                                <select
                                  className='form-control form-select'
                                  value={form.birthYear}
                                  onChange={setField("birthYear")}
                                >
                                  <option value=''>{t("global.birth.year")}</option>
                                  {Array.from({ length: 30 }, (_, i) => currentYear - i).map((year) => (
                                    <option key={year} value={year}>
                                      {year}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </div>
                          <div className='col-sm-4'>
                            <label className='form-label'>{t("global.gender")} <span className='required-asterisk'>*</span></label>
                            <select
                              className='form-control form-select'
                              value={form.gender}
                              onChange={setField("gender")}
                            >
                              <option value=''>{t("estudante.select.placeholder")}</option>
                              <option value='M'>{t("global.male")}</option>
                              <option value='F'>{t("global.female")}</option>
                            </select>
                          </div>
                          <div className='col-sm-4'>
                            <label className='form-label'>{t("estudante.field.docType")} <span className='required-asterisk'>*</span></label>
                            <select
                              className='form-control form-select'
                              value={form.docType}
                              onChange={setField("docType")}
                            >
                              <option value=''>{t("estudante.select.placeholder")}</option>
                              <option value='BI'>{t("estudante.docType.bi")}</option>
                              <option value='Passaporte'>{t("estudante.docType.passport")}</option>
                              <option value='Cedula'>{t("estudante.docType.cedula")}</option>
                              <option value='Bolentim'>{t("estudante.docType.bolentim")}</option>
                              <option value='Nenhum'>{t("estudante.docType.none")}</option>
                            </select>
                          </div>
                          {form.docType && form.docType !== "Nenhum" && (
                            <div className='col-sm-6'>
                              <label className='form-label'>{t("estudante.field.docNumber")} <span className='required-asterisk'>*</span></label>
                              <div className="icon-field">
                                <span className="icon">
                                  <Icon icon="solar:card-2-outline" />
                                </span>
                                <input
                                  type='text'
                                  className='form-control'
                                  value={form.docNumber}
                                  onChange={setField("docNumber")}
                                />
                              </div>
                            </div>
                          )}
                          <div className='col-12'>
                            <div className='form-check'>
                              <input
                                id='isNew'
                                type='checkbox'
                                className='form-check-input'
                                checked={form.isNew}
                                onChange={setChecked("isNew")}
                              />
                              <label htmlFor='isNew' className='form-check-label'>
                                {t("estudante.field.isNew")}
                              </label>
                            </div>
                          </div>
                          {!form.isNew && (
                            <div className='col-sm-4'>
                              <label className='form-label'>
                                {t("estudante.field.enrollmentYear")}*
                              </label>
                              <select
                                className='form-control form-select'
                                value={form.enrollmentYear}
                                onChange={setField("enrollmentYear")}
                              >
                                {Array.from(
                                  { length: currentYear - 2018 + 1 },
                                  (_, i) => 2018 + i,
                                ).map((y) => (
                                  <option key={y} value={y}>
                                    {y}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                      </StepCard>

                      <StepCard
                        icon='solar:map-point-outline'
                        title={t("estudante.personal.address.title")}
                        accent='info'
                      >
                        <div className='row gy-3'>
                          <div className='col-sm-12'>
                            <label className='form-label'>
                              {t("estudante.field.studentAddress")}*
                            </label>
                            <textarea
                              rows={3}
                              className='form-control'
                              placeholder={t("estudante.field.studentAddress.placeholder")}
                              value={form.studentAddress}
                              onChange={setField("studentAddress")}
                            />
                          </div>
                          <div className='col-sm-4'>
                            <label className='form-label'>{t("estudante.field.hasDesease")}</label>
                            <select
                              className='form-control form-select'
                              value={form.hasDesease}
                              onChange={setField("hasDesease")}
                            >
                              <option value='0'>{t("global.no")}</option>
                              <option value='1'>{t("global.yes")}</option>
                            </select>
                          </div>
                          {form.hasDesease === "1" && (
                            <div className='col-sm-8'>
                              <label className='form-label'>
                                {t("estudante.field.deseaseDesc")}*
                              </label>
                              <textarea
                                rows={2}
                                className='form-control'
                                value={form.deseaseDesc}
                                onChange={setField("deseaseDesc")}
                              />
                            </div>
                          )}
                          <div className='col-12'>
                            <div className='d-flex flex-wrap gap-24'>
                              <div className='form-check'>
                                <input
                                  id='isAlergicToFood'
                                  type='checkbox'
                                  className='form-check-input'
                                  checked={form.isAlergicToFood}
                                  onChange={setChecked("isAlergicToFood")}
                                />
                                <label htmlFor='isAlergicToFood' className='form-check-label'>
                                  {t("estudante.field.isAlergicToFood")}
                                </label>
                              </div>
                              <div className='form-check'>
                                <input
                                  id='isAlergicToMedicine'
                                  type='checkbox'
                                  className='form-check-input'
                                  checked={form.isAlergicToMedicine}
                                  onChange={setChecked("isAlergicToMedicine")}
                                />
                                <label htmlFor='isAlergicToMedicine' className='form-check-label'>
                                  {t("estudante.field.isAlergicToMedicine")}
                                </label>
                              </div>
                            </div>
                          </div>
                          {form.isAlergicToFood && (
                            <div className='col-sm-6'>
                              <label className='form-label'>
                                {t("estudante.field.alergicToFood")}
                              </label>
                              <textarea
                                rows={2}
                                className='form-control'
                                value={form.alergicToFood}
                                onChange={setField("alergicToFood")}
                              />
                            </div>
                          )}
                          {form.isAlergicToMedicine && (
                            <div className='col-sm-6'>
                              <label className='form-label'>
                                {t("estudante.field.alergicToMedicine")}
                              </label>
                              <textarea
                                rows={2}
                                className='form-control'
                                value={form.alergicToMedicine}
                                onChange={setField("alergicToMedicine")}
                              />
                            </div>
                          )}
                        </div>
                      </StepCard>

                      <div className='form-group text-end'>
                        <button
                          onClick={nextStep}
                          type='button'
                          className='form-wizard-next-btn btn btn-primary-600 px-32'
                        >
                          {t("global.next")} <i className='ri-arrow-right-line' />
                        </button>
                      </div>
                    </div>
                  </div>
                </fieldset>
              )}

              {!done && currentStep === 2 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='mb-24'>
                    <h6 className='text-md mb-4'>{t("estudante.wizard.step.filiation")}</h6>
                    <p className='text-secondary-light text-sm mb-0'>
                      {t("estudante.filiation.subtitle")}
                    </p>
                  </div>

                  <StepCard icon='f7:person-2' title={t("estudante.wizard.step.filiation")} accent='warning'>
                    <div className='row gy-3'>
                      <div className='col-sm-6'>
                        <label className='form-label'>{t("estudante.field.fatherName")} <span className='required-asterisk'>*</span></label>
                        <div className="icon-field">
                          <span className="icon">
                            <Icon icon="f7:person" />
                          </span>
                          <input
                            type='text'
                            className='form-control'
                            value={form.fatherName}
                            onChange={setField("fatherName")}
                          />
                        </div>
                      </div>
                      <div className='col-sm-6'>
                        <label className='form-label'>{t("estudante.field.motherName")} <span className='required-asterisk'>*</span></label>
                        <div className="icon-field">
                          <span className="icon">
                            <Icon icon="f7:person" />
                          </span>
                          <input
                            type='text'
                            className='form-control'
                            value={form.motherName}
                            onChange={setField("motherName")}
                          />
                        </div>
                      </div>
                      <div className='col-sm-6'>
                        <label className='form-label'>{t("estudante.field.fatherContact")}</label>
                        <div className='input-group'>
                          <span className='input-group-text'>+258</span>
                          <input
                            type='text'
                            maxLength={9}
                            className='form-control'
                            value={form.fatherContact}
                            onChange={setField("fatherContact")}
                          />
                        </div>
                      </div>
                      <div className='col-sm-6'>
                        <label className='form-label'>{t("estudante.field.motherContact")}</label>
                        <div className='input-group'>
                          <span className='input-group-text'>+258</span>
                          <input
                            type='text'
                            maxLength={9}
                            className='form-control'
                            value={form.motherContact}
                            onChange={setField("motherContact")}
                          />
                        </div>
                      </div>
                    </div>
                  </StepCard>

                  <div className='form-group d-flex align-items-center justify-content-end gap-8'>
                    <button
                      onClick={prevStep}
                      type='button'
                      className='form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32'
                    >
                      {t("global.previous")}
                    </button>
                    <button
                      onClick={nextStep}
                      type='button'
                      className='form-wizard-next-btn btn btn-primary-600 px-32'
                    >
                      {t("global.next")} <i className='ri-arrow-right-line' />
                    </button>
                  </div>
                </fieldset>
              )}

              {!done && currentStep === 3 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='mb-24'>
                    <h6 className='text-md mb-4'>{t("estudante.wizard.step.guardian")}</h6>
                    <p className='text-secondary-light text-sm mb-0'>
                      {t("estudante.guardian.subtitle")}
                    </p>
                  </div>

                  <StepCard
                    icon='solar:shield-user-outline'
                    title={t("estudante.wizard.step.guardian")}
                    accent='info'
                  >
                    <div className='row gy-3'>
                      <div className='col-sm-6'>
                        <label className='form-label'>{t("estudante.field.kinshipDegree")} <span className='required-asterisk'>*</span></label>
                        <select
                          className='form-control form-select'
                          value={form.kinshipDegree}
                          onChange={handleSelectKinship}
                        >
                          <option value=''>{t("estudante.select.placeholder")}</option>
                          <option value='PAI'>{t("estudante.kinship.father")}</option>
                          <option value='MAE'>{t("estudante.kinship.mother")}</option>
                          <option value='TIO'>{t("estudante.kinship.uncle")}</option>
                          <option value='AVO'>{t("estudante.kinship.grandparent")}</option>
                          <option value='IRMAO'>{t("estudante.kinship.sibling")}</option>
                        </select>
                      </div>
                      <div className='col-sm-6'>
                        <label className='form-label'>{t("estudante.field.carierName")} <span className='required-asterisk'>*</span></label>
                        <div className="icon-field">
                          <span className="icon">
                            <Icon icon="f7:person" />
                          </span>
                          <input
                            type='text'
                            className='form-control'
                            value={form.carierName}
                            onChange={setField("carierName")}
                          />
                        </div>
                      </div>
                      <div className='col-sm-6'>
                        <label className='form-label'>{t("estudante.field.jobLocation")}</label>
                        <div className="icon-field">
                          <span className="icon">
                            <Icon icon="solar:map-point-outline" />
                          </span>
                          <input
                            type='text'
                            className='form-control'
                            value={form.jobLocation}
                            onChange={setField("jobLocation")}
                          />
                        </div>
                      </div>
                      <div className='col-sm-6'>
                        <label className='form-label'>{t("estudante.field.carierContact")} <span className='required-asterisk'>*</span></label>
                        <div className='input-group'>
                          <span className='input-group-text'>+258</span>
                          <input
                            type='text'
                            maxLength={9}
                            className='form-control'
                            value={form.carierContact}
                            onChange={setField("carierContact")}
                          />
                        </div>
                      </div>
                      <div className='col-sm-6'>
                        <label className='form-label'>
                          {t("estudante.field.secondCarierContact")}
                        </label>
                        <div className='input-group'>
                          <span className='input-group-text'>+258</span>
                          <input
                            type='text'
                            maxLength={9}
                            className='form-control'
                            value={form.carierSecondContact}
                            onChange={setField("carierSecondContact")}
                          />
                        </div>
                      </div>
                      <div className='col-sm-12'>
                        <label className='form-label'>{t("estudante.field.address")} <span className='required-asterisk'>*</span></label>
                        <textarea
                          rows={3}
                          className='form-control'
                          value={form.address}
                          onChange={setField("address")}
                        />
                      </div>
                    </div>
                  </StepCard>

                  <div className='form-group d-flex align-items-center justify-content-end gap-8'>
                    <button
                      onClick={prevStep}
                      type='button'
                      className='form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32'
                    >
                      {t("global.previous")}
                    </button>
                    <button
                      onClick={nextStep}
                      type='button'
                      className='form-wizard-next-btn btn btn-primary-600 px-32'
                    >
                      {t("global.next")} <i className='ri-arrow-right-line' />
                    </button>
                  </div>
                </fieldset>
              )}

              {!done && currentStep === 4 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='mb-24'>
                    <h6 className='text-md mb-4'>{t("estudante.wizard.step.enrollment")}</h6>
                    <p className='text-secondary-light text-sm mb-0'>
                      {t("estudante.enrollment.details.subtitle")}
                    </p>
                  </div>

                  <StepCard
                    icon='solar:notebook-bookmark-outline'
                    title={t("estudante.enrollment.details.title")}
                    accent='primary'
                  >
                    <div className='row gy-3'>
                      <div className='col-sm-4'>
                        <label className='form-label'>{t("estudante.field.selectedYear")} <span className='required-asterisk'>*</span></label>
                        <select
                          className='form-control form-select'
                          value={form.selectedYear}
                          onChange={setField("selectedYear")}
                        >
                          {Array.from({ length: currentYear + 1 - 2024 + 1 }, (_, i) => 2024 + i).map(
                            (y) => (
                              <option key={y} value={y}>
                                {y}
                              </option>
                            ),
                          )}
                        </select>
                      </div>
                      <div className='col-sm-4'>
                        <label className='form-label'>{t(levelKey("estudante.field.frequency"))} <span className='required-asterisk'>*</span></label>
                        <select
                          className='form-control form-select'
                          value={form.frequency}
                          onChange={setField("frequency")}
                        >
                          <option value=''>{t("estudante.select.placeholder")}</option>
                          {frequencies.map((f) => (
                            <option key={f.id} value={f.id}>
                              {f.description}
                            </option>
                          ))}
                        </select>
                      </div>
                      {hasSpecialHourSchedule() && (
                        <div className='col-sm-4'>
                          <label className='form-label'>{t("estudante.field.scheduleType")}</label>
                          <select
                            className='form-control form-select'
                            value={form.scheduleType}
                            onChange={setField("scheduleType")}
                          >
                            <option value='7h-12h'>{t("estudante.field.scheduleType.standard")}</option>
                            <option value='7h-15h'>{t("estudante.field.scheduleType.special")}</option>
                          </select>
                        </div>
                      )}
                      <div className='col-sm-12'>
                        <label className='form-label'>
                          {t("estudante.field.extraCuricularActivities")}
                        </label>
                        <MultiSelectField
                          items={activitiesForFrequency}
                          getOptionValue={(item) => String(item.id)}
                          getOptionLabel={(item) => item.name}
                          selectedIds={form.extraCuricularActivities}
                          onChange={(ids) =>
                            setForm((f) => ({ ...f, extraCuricularActivities: ids }))
                          }
                          placeholder={t("estudante.select.placeholder")}
                          noOptionsMessage={t("estudante.field.extraCuricularActivities.empty")}
                        />
                        <span className='text-secondary-light text-sm'>
                          {t("estudante.field.extraCuricularActivities.hint")}
                        </span>
                      </div>
                    </div>
                  </StepCard>

                  <StepCard
                    icon='solar:wallet-money-outline'
                    title={t("estudante.registrationPayment.title")}
                    subtitle={t("estudante.registrationPayment.subtitle")}
                    accent='success'
                  >
                    {/* Bolsa e Desconto */}
                    <div className='mb-20'>
                      <span className='text-secondary-light text-sm fw-semibold d-block mb-12'>
                        {t("estudante.scholarship.title")}
                      </span>
                      <div className='row gy-3'>
                        <div className='col-sm-6'>
                          <label className='form-label'>{t("estudante.scholarship.type")}</label>
                          <select
                            className='form-control form-select'
                            value={form.scholarshipType}
                            onChange={handleScholarshipType}
                          >
                            <option value='none'>{t("estudante.scholarship.type.none")}</option>
                            <option value='discount'>{t("estudante.scholarship.type.discount")}</option>
                            <option value='partial'>{t("estudante.scholarship.type.partial")}</option>
                            <option value='full'>{t("estudante.scholarship.type.full")}</option>
                          </select>
                        </div>

                        {form.scholarshipType === "full" && (
                          <>
                            <div className='col-12'>
                              <div className='alert alert-success mb-0'>
                                {t("estudante.scholarship.full.info")}
                              </div>
                            </div>
                            <div className='col-12'>
                              <div className='form-check'>
                                <input
                                  id='exemptRegistration'
                                  type='checkbox'
                                  className='form-check-input'
                                  checked={form.exemptRegistration}
                                  onChange={setChecked("exemptRegistration")}
                                />
                                <label htmlFor='exemptRegistration' className='form-check-label'>
                                  {t("estudante.scholarship.exemptRegistration")}
                                </label>
                              </div>
                            </div>
                            {!form.exemptRegistration && (
                              <div className='col-sm-6'>
                                <label className='form-label'>
                                  {t("estudante.scholarship.registrationDiscount")}
                                </label>
                                <select
                                  className='form-control form-select'
                                  value={form.registrationDiscount}
                                  onChange={setField("registrationDiscount")}
                                >
                                  {DISCOUNT_OPTIONS.map((opt) => (
                                    <option key={opt} value={opt}>
                                      {Math.round(opt * 100)}%
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </>
                        )}

                        {(form.scholarshipType === "partial" || form.scholarshipType === "discount") && (
                          <>
                            <div className='col-sm-6'>
                              <label className='form-label'>{t("estudante.discount.type")}</label>
                              <select
                                className='form-control form-select'
                                value={form.discountType}
                                onChange={setField("discountType")}
                              >
                                <option value='percentage'>{t("estudante.discount.type.percentage")}</option>
                                <option value='numeric'>{t("estudante.discount.type.numeric")}</option>
                              </select>
                            </div>
                            <div className='col-sm-6'>
                              <label className='form-label'>{t("estudante.discount.appliesTo")}</label>
                              <select
                                className='form-control form-select'
                                value={form.discountAppliesTo}
                                onChange={setField("discountAppliesTo")}
                              >
                                <option value='registration'>
                                  {t("estudante.discount.appliesTo.registration")}
                                </option>
                                <option value='payment'>
                                  {t("estudante.discount.appliesTo.payment")}
                                </option>
                                <option value='both'>{t("estudante.discount.appliesTo.both")}</option>
                              </select>
                            </div>

                            {form.discountAppliesTo === "both" ? (
                              <>
                                <div className='col-sm-6'>
                                  <label className='form-label'>
                                    {t("estudante.discount.registration")}
                                  </label>
                                  {form.discountType === "percentage" ? (
                                    <select
                                      className='form-control form-select'
                                      value={form.registrationDiscount}
                                      onChange={setField("registrationDiscount")}
                                    >
                                      {DISCOUNT_OPTIONS.map((opt) => (
                                        <option key={opt} value={opt}>
                                          {Math.round(opt * 100)}%
                                        </option>
                                      ))}
                                    </select>
                                  ) : (
                                    <div className="icon-field">
                                      <span className="icon">
                                        <Icon icon="solar:pen-2-outline" />
                                      </span>
                                      <input
                                        type='number'
                                        className='form-control'
                                        value={form.registrationDiscount}
                                        onChange={setField("registrationDiscount")}
                                      />
                                    </div>
                                  )}
                                </div>
                                <div className='col-sm-6'>
                                  <label className='form-label'>{t("estudante.discount.monthly")}</label>
                                  {form.discountType === "percentage" ? (
                                    <select
                                      className='form-control form-select'
                                      value={form.monthlyDiscount}
                                      onChange={setField("monthlyDiscount")}
                                    >
                                      {DISCOUNT_OPTIONS.map((opt) => (
                                        <option key={opt} value={opt}>
                                          {Math.round(opt * 100)}%
                                        </option>
                                      ))}
                                    </select>
                                  ) : (
                                    <div className="icon-field">
                                      <span className="icon">
                                        <Icon icon="solar:pen-2-outline" />
                                      </span>
                                      <input
                                        type='number'
                                        className='form-control'
                                        value={form.monthlyDiscount}
                                        onChange={setField("monthlyDiscount")}
                                      />
                                    </div>
                                  )}
                                </div>
                              </>
                            ) : (
                              <div className='col-sm-6'>
                                <label className='form-label'>{t("estudante.discount.value")}</label>
                                {form.discountType === "percentage" ? (
                                  <select
                                    className='form-control form-select'
                                    value={form.discount}
                                    onChange={setField("discount")}
                                  >
                                    {DISCOUNT_OPTIONS.map((opt) => (
                                      <option key={opt} value={opt}>
                                        {Math.round(opt * 100)}%
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  <div className="icon-field">
                                    <span className="icon">
                                      <Icon icon="solar:pen-2-outline" />
                                    </span>
                                    <input
                                      type='number'
                                      className='form-control'
                                      value={form.discount}
                                      onChange={setField("discount")}
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    <hr className='my-20' />

                    {/* Forma de pagamento e valores */}
                    <div className='row gy-3'>
                      <div className='col-sm-6'>
                        <label className='form-label'>{t("estudante.field.paymentMethod")} <span className='required-asterisk'>*</span></label>
                        <select
                          className='form-control form-select'
                          value={form.paymentMethod}
                          onChange={setField("paymentMethod")}
                        >
                          <option value=''>{t("estudante.select.placeholder")}</option>
                          {paymentMethods.map((m) => (
                            <option key={m.id} value={m.name}>
                              {m.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className='col-sm-3'>
                        <div className='p-16 radius-8 bg-base border border-neutral-100 text-center h-100'>
                          <span className='text-secondary-light text-sm d-block'>
                            {t("estudante.registrationPayment.amountDue")}
                          </span>
                          <strong className='text-primary-600'>{money(form.registrationValue)}</strong>
                        </div>
                      </div>
                      <div className='col-sm-3'>
                        <label className='form-label'>
                          {t("estudante.registrationPayment.amountPaid")}*
                        </label>
                        <input
                          type='number'
                          className='form-control'
                          min={0}
                          max={form.registrationValue}
                          value={form.registrationAmountPaid}
                          onChange={(e) => {
                            const raw = parseFloat(e.target.value);
                            const max = parseFloat(form.registrationValue) || 0;
                            const clamped = Number.isNaN(raw) ? 0 : Math.max(0, Math.min(raw, max));
                            setForm((f) => ({ ...f, registrationAmountPaid: clamped }));
                          }}
                        />
                      </div>
                      <div className='col-12'>
                        <div
                          className={`p-12 radius-8 text-center ${
                            parseFloat(form.registrationAmountPaid) <
                            parseFloat(form.registrationValue) - 0.01
                              ? "bg-danger-50"
                              : "bg-success-50"
                          }`}
                        >
                          <span
                            className={`text-sm d-block ${
                              parseFloat(form.registrationAmountPaid) <
                              parseFloat(form.registrationValue) - 0.01
                                ? "text-danger-600"
                                : "text-success-600"
                            }`}
                          >
                            {t("propina.field.amountToPay")}
                          </span>
                          <strong
                            className={
                              parseFloat(form.registrationAmountPaid) <
                              parseFloat(form.registrationValue) - 0.01
                                ? "text-danger-600"
                                : "text-success-600"
                            }
                          >
                            {money(
                              Math.max(
                                0,
                                parseFloat(form.registrationValue) -
                                  parseFloat(form.registrationAmountPaid),
                              ),
                            )}
                          </strong>
                        </div>
                      </div>
                    </div>
                  </StepCard>

                  <div className='row gy-3 mb-24'>
                    <div className='col-sm-6'>
                      <div className='p-16 radius-8 bg-neutral-50 text-center'>
                        <span className='text-secondary-light text-sm d-block'>
                          {t("estudante.summary.total")}
                        </span>
                        <strong className='text-success-600'>{money(form.total)}</strong>
                      </div>
                    </div>
                  </div>

                  <div className='form-group d-flex align-items-center justify-content-end gap-8'>
                    <button
                      onClick={prevStep}
                      type='button'
                      className='form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32'
                    >
                      {t("global.previous")}
                    </button>
                    <button
                      onClick={nextStep}
                      type='button'
                      className='form-wizard-next-btn btn btn-primary-600 px-32'
                    >
                      {t("global.next")} <i className='ri-arrow-right-line' />
                    </button>
                  </div>
                </fieldset>
              )}

              {!done && currentStep === 5 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='mb-24'>
                    <h6 className='text-md mb-4'>{t("estudante.wizard.step.confirm")}</h6>
                    <p className='text-secondary-light text-sm mb-0'>
                      {t("estudante.confirm.description")}
                    </p>
                  </div>

                  <div className='row gy-4'>
                    <div className='col-lg-2 col-md-3 text-center'>
                      <div className='w-120-px h-120-px rounded-circle overflow-hidden mx-auto d-flex align-items-center justify-content-center bg-neutral-50 border border-3 border-primary-50'>
                        {form.imagePreview ? (
                          <img
                            src={form.imagePreview}
                            alt=''
                            className='w-100 h-100 object-fit-cover'
                          />
                        ) : (
                          <Icon
                            icon='solar:user-rounded-outline'
                            className='text-neutral-300'
                            style={{ fontSize: 48 }}
                          />
                        )}
                      </div>
                      <h6 className='text-sm mt-12 mb-0'>{form.name}</h6>
                      {form.code && (
                        <span className='text-secondary-light text-xs'>{form.code}</span>
                      )}
                    </div>

                    <div className='col-lg-10 col-md-9'>
                      <div className='row gy-3'>
                        <ReviewCard icon='f7:person' title={t("estudante.wizard.step.personal")}>
                          <ReviewRow label={t("estudante.field.name")} value={form.name} />
                          <ReviewRow label={t("global.birth.date")} value={birthDate} />
                          <ReviewRow
                            label={t("global.gender")}
                            value={form.gender === "M" ? t("global.male") : t("global.female")}
                          />
                          <ReviewRow
                            label={t("estudante.field.docType")}
                            value={
                              form.docType === "Nenhum"
                                ? t("estudante.docType.none")
                                : `${form.docType} ${form.docNumber || ""}`.trim()
                            }
                          />
                          <ReviewRow
                            label={t("estudante.field.studentAddress")}
                            value={form.studentAddress}
                          />
                          {form.hasDesease === "1" && (
                            <ReviewRow
                              label={t("estudante.field.deseaseDesc")}
                              value={form.deseaseDesc}
                            />
                          )}
                        </ReviewCard>

                        <ReviewCard icon='f7:person-2' title={t("estudante.wizard.step.filiation")}>
                          <ReviewRow label={t("estudante.field.fatherName")} value={form.fatherName} />
                          <ReviewRow
                            label={t("estudante.field.fatherContact")}
                            value={form.fatherContact && `+258 ${form.fatherContact}`}
                          />
                          <ReviewRow label={t("estudante.field.motherName")} value={form.motherName} />
                          <ReviewRow
                            label={t("estudante.field.motherContact")}
                            value={form.motherContact && `+258 ${form.motherContact}`}
                          />
                        </ReviewCard>

                        <ReviewCard
                          icon='solar:shield-user-outline'
                          title={t("estudante.wizard.step.guardian")}
                        >
                          <ReviewRow label={t("estudante.field.carierName")} value={form.carierName} />
                          <ReviewRow
                            label={t("estudante.field.carierContact")}
                            value={form.carierContact && `+258 ${form.carierContact}`}
                          />
                          <ReviewRow
                            label={t("estudante.field.secondCarierContact")}
                            value={form.carierSecondContact && `+258 ${form.carierSecondContact}`}
                          />
                          <ReviewRow label={t("estudante.field.jobLocation")} value={form.jobLocation} />
                          <ReviewRow label={t("estudante.field.address")} value={form.address} />
                        </ReviewCard>

                        <ReviewCard
                          icon='solar:document-add-outline'
                          title={t("estudante.wizard.step.enrollment")}
                        >
                          <ReviewRow
                            label={t("estudante.field.selectedYear")}
                            value={form.selectedYear}
                          />
                          <ReviewRow
                            label={t(levelKey("estudante.field.frequency"))}
                            value={selectedFrequency?.description}
                          />
                          <ReviewRow
                            label={t("estudante.field.paymentMethod")}
                            value={form.paymentMethod}
                          />
                          <ReviewRow
                            label={t("estudante.field.extraCuricularActivities")}
                            value={selectedActivityNames}
                          />
                          {form.scholarshipType !== "none" && (
                            <ReviewRow
                              label={t("estudante.field.scholarshipType")}
                              value={scholarshipLabels[form.scholarshipType]}
                            />
                          )}
                          <ReviewRow
                            label={t("estudante.registrationPayment.amountPaid")}
                            value={money(form.registrationAmountPaid)}
                          />
                          {parseFloat(form.registrationAmountPaid) <
                            parseFloat(form.registrationValue) - 0.01 && (
                            <div className='d-flex align-items-center justify-content-between gap-12 py-4'>
                              <span className='text-danger-600 text-sm'>
                                {t("estudante.registrationPayment.remaining")}
                              </span>
                              <span className='text-danger-600 text-sm fw-semibold'>
                                {money(
                                  parseFloat(form.registrationValue) -
                                    parseFloat(form.registrationAmountPaid),
                                )}
                              </span>
                            </div>
                          )}
                        </ReviewCard>
                      </div>

                      <div className='row gy-3 mt-8'>
                        <div className='col-sm-6'>
                          <div className='p-16 radius-8 bg-neutral-50 text-center'>
                            <span className='text-secondary-light text-sm d-block'>
                              {t("estudante.summary.registrationValue")}
                            </span>
                            <strong className='text-primary-600'>
                              {money(form.registrationValue)}
                            </strong>
                          </div>
                        </div>
                        <div className='col-sm-6'>
                          <div className='p-16 radius-8 bg-neutral-50 text-center'>
                            <span className='text-secondary-light text-sm d-block'>
                              {t("estudante.summary.total")}
                            </span>
                            <strong className='text-success-600'>{money(form.total)}</strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-end gap-8 mt-24'>
                    <button
                      onClick={prevStep}
                      type='button'
                      className='form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32'
                      disabled={submitting}
                    >
                      {t("global.previous")}
                    </button>
                    <button
                      onClick={submit}
                      type='button'
                      className='form-wizard-submit btn btn-primary-600 px-32'
                      disabled={submitting}
                    >
                      {submitting ? (
                        "..."
                      ) : (
                        <>
                          <i className='ri-check-line' /> {t("global.confirm")}
                        </>
                      )}
                    </button>
                  </div>
                </fieldset>
              )}

              {done && (
                <fieldset className='wizard-fieldset show'>
                  <div className='text-center mb-40'>
                    <span className='w-64-px h-64-px bg-success-focus text-success-main rounded-circle d-inline-flex justify-content-center align-items-center mb-16 text-2xl'>
                      <i className='ri-check-line' />
                    </span>
                    <h6 className='text-md text-neutral-600'>
                      {t("estudante.create.success.title")}
                    </h6>
                    {form.name && (
                      <p className='text-primary-600 fw-semibold text-sm mb-4'>{form.name}</p>
                    )}
                    <p className='text-neutral-400 text-sm mb-0'>
                      {t("estudante.create.success.subtitle")}
                    </p>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-center gap-8'>
                    <button
                      type='button'
                      className='btn btn-outline-primary-600 px-32'
                      onClick={handlePrintComprovativo}
                      disabled={printing}
                    >
                      {printing ? "..." : t("estudante.create.print")}
                    </button>
                    <button
                      type='button'
                      className='btn btn-neutral-500 border-neutral-100 px-32'
                      onClick={restart}
                    >
                      {t("estudante.create.new")}
                    </button>
                    <button
                      type='button'
                      className='btn btn-primary-600 px-32'
                      onClick={() => navigate("/estudantes/activos")}
                    >
                      <i className='ri-arrow-left-line' /> {t("estudante.breadcrumb.list")}
                    </button>
                  </div>
                </fieldset>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CadastrarEstudanteLayer;
